import { type MouseEvent, useMemo, useState } from "react";

import { ChartCard } from "../components/ChartCard.js";
import { BarChart } from "../components/charts/BarChart.js";
import { GameMinutesShareCard } from "../components/dashboard/GameMinutesShareCard.js";
import { GamesCompletedByGameCard } from "../components/dashboard/GamesCompletedByGameCard.js";
import { KpiRow } from "../components/KpiRow.js";
import { gameFilterValueFromConceptKey } from "../game-filter-param.js";
import { navigateOrOpenNewTab } from "../lib/dashboardNav.js";
import { gameColorFromConceptKey, gameDisplayLabel } from "../lib/gamePalette.js";
import { buildRawEventsUrlFromOptions } from "../lib/rawEventsUrl.js";
import type {
  EngagementData,
  FilterState,
  OpenRawEventsHandler,
  OpenRawEventsOptions,
} from "../types.js";

interface EngagementPanelProps {
  data: EngagementData | null;
  filters: FilterState;
  onOpenRawEvents: OpenRawEventsHandler;
}

export function EngagementPanel({ data, filters, onOpenRawEvents }: EngagementPanelProps) {
  if (!data) {
    return <p className="py-5 text-[13px] text-text-secondary">No data available</p>;
  }

  const topGameDisplay = data.topGameLabel
    ? `${gameDisplayLabel(data.topGameLabel)} (${data.topGameMinutes.toFixed(1)}m)`
    : "n/a";
  const avgLoadDisplay =
    data.avgLoadTimeMs > 0 ? `${(data.avgLoadTimeMs / 1000).toFixed(2)}s` : "n/a";
  const surveyVolume = data.surveyVolume;
  let surveyAvgRating: number | null = null;
  if (surveyVolume > 0) {
    let weighted = 0;
    for (let i = 0; i < 5; i++) {
      weighted += (i + 1) * (data.surveyRatings[i] ?? 0);
    }
    surveyAvgRating = weighted / surveyVolume;
  }
  const surveyKpiDisplay =
    surveyAvgRating != null
      ? `${surveyAvgRating.toFixed(1)} (${surveyVolume.toLocaleString()})`
      : "n/a";

  return (
    <>
      <KpiRow
        items={[
          { name: "Events in scope", value: String(data.eventCount), sub: "metrics-filtered" },
          { name: "Top game minutes", value: topGameDisplay, sub: "strongest concept now" },
          { name: "Avg load time", value: avgLoadDisplay, sub: "mean load across games with data" },
          {
            name: "Avg Rating (Survey volume)",
            value: surveyKpiDisplay,
            sub: "mean unique survey rating (2 min burst dedupe; last rating wins)",
          },
        ]}
      />

      <div className="mb-2 text-sm font-bold text-text-primary">What games they’re playing</div>
      <div className="grid grid-cols-1 gap-[10px] xl:grid-cols-2">
        <GameMinutesShareCard
          byGameMinutesPct={data.byGameMinutesPct}
          totalPlayMinutes={data.totalPlayMinutes}
          onOpenRawEvents={onOpenRawEvents}
        />

        <GamesCompletedByGameCard
          gamesCompletedByGame={data.gamesCompletedByGame}
          totalGamesCompleted={data.totalGamesCompleted}
          totalGamesAbandoned={data.totalGamesAbandoned}
          onOpenRawEvents={onOpenRawEvents}
        />

        <ChartCard
          title="Minutes per Start by Game"
          criteria={`Numerator: sum of **screen_exited.duration_ms** where **screen_name** = **game**, grouped by canonical playable key (\`/games/{slug}\` from route or slugifiable game_name). Denominator: count of **game_started** / **game_start** for the same key.

Bar click: Raw Events → game_started with game_url = this game's route.`}
          note="Lower = less play per start (icon or premise may under-sell)."
        >
          <BarChart
            data={[...data.minutesPerStart].sort((a, b) => b.value - a.value).map((x) => ({
              label: x.game,
              value: x.value,
              fill: gameColorFromConceptKey(x.game),
            }))}
            barTooltip={(item) =>
              `${gameDisplayLabel(item.label)}: ${item.value.toFixed(2)} min per start — click: Raw Events → game_started for this route. Middle-click / Ctrl/Cmd+click: new tab.`
            }
            onBarClick={(item, _i, _si, e) =>
              onOpenRawEvents(
                { eventName: "game_started", gameUrl: gameFilterValueFromConceptKey(item.label) },
                e,
              )
            }
          />
        </ChartCard>

        <ChartCard
          title="Avg Time To Load per Game"
          criteria={`Source: device_events where event_name = 'game_load_completed' and properties.load_ms is not null.

Per bar: avg(load_ms::numeric) grouped by canonical playable key (\`/games/{slug}\`).

Labels above bars: average load in seconds to 2 decimal places with an “s” suffix.

Bar click: Raw Events → game_load_completed + game_url filter for that route.`}
          note="Tracks which game routes feel slow to users before play begins."
        >
          <BarChart
            data={data.avgLoadMsByGame.map((x) => ({
              label: x.game,
              value: x.value,
              valueLabel: x.valueLabel,
              fill: gameColorFromConceptKey(x.game),
            }))}
            barTooltip={(item) =>
              `Avg load ${item.valueLabel ?? `${(item.value / 1000).toFixed(2)}s`} — click: Raw Events → game_load_completed for ${item.label}. Middle-click / Ctrl/Cmd+click: new tab.`
            }
            onBarClick={(item, _i, _si, e) =>
              onOpenRawEvents(
                { eventName: "game_load_completed", gameUrl: gameFilterValueFromConceptKey(item.label) },
                e,
              )
            }
          />
        </ChartCard>

        <ChartCard
          title="Session Play-Time Histogram"
          criteria={`Source: device_events where event_name = 'screen_exited', **screen_name** = **game**.

Bucket = **duration_ms** on each game-screen exit (same shell dwell as session journey / Game Minutes pie).

Bar click: Raw Events → screen_exited with **screen_name** = game.`}
          note="Highlights short-bounce vs high-engagement sessions."
        >
          <BarChart
            data={[
              { label: "<10s", value: data.sessionHist[0] ?? 0 },
              { label: "10-30s", value: data.sessionHist[1] ?? 0 },
              { label: "30s-2m", value: data.sessionHist[2] ?? 0 },
              { label: "2-5m", value: data.sessionHist[3] ?? 0 },
              { label: "5m+", value: data.sessionHist[4] ?? 0 },
            ]}
            barTooltip={(item) =>
              `${item.label}: ${Math.round(item.value)} sessions — same load→end span as Game Minutes; click: Raw Events → game_completed (includes game_abandoned). Middle-click / Ctrl/Cmd+click: new tab.`
            }
            onBarClick={(_item, _i, _si, e) =>
              onOpenRawEvents(
                { eventName: "screen_exited", propertiesContains: `"screen_name":"game"` },
                e,
              )
            }
          />
        </ChartCard>

        <ChartCard
          title="Concept Ranking"
          criteria={`Row key = session-first route from the earliest game_started / game_start / game_selected in each interaction_session_id, else game_name, else resolved route (see session-first-route.ts). Used for menu-intent concept comparison only — not the per-game bar charts above.

Rows sortable via column headers (default: min/start high → low).

• Icon clicks — **popup_open** with **popup** = tutorial and **popup_surface** = **main_menu** (shell menu tutorial entry only; excludes e.g. **game_chrome** in-game tutorials). Each matching open = one count.

• Tutorial % — **popup_closed** with **popup** = tutorial and **popup_surface** = **main_menu**: **result** = play vs dismiss (case-insensitive); % = 100×play÷(play+dismiss); cell shows **% (play/dismiss)**.

• Surveys per game — deduped **survey_submit** with non-null **rating**: one **unique survey** per **device + interaction_session_id + game route/name** when submits are ≤ **2 minutes** apart; **last rating in the burst** wins (e.g. spam 5,4,1,3,4 → counts as one survey rated **4**). Gap **> 2 min** = another unique survey. Grouped by row **game_route** (else **game_name**). **Raw Events** still lists every row.

• Rating — average of those last-in-burst ratings per game. Joined to concept rows by matching playable key string.

Play minutes ÷ starts on this chart also use the session-first row key. Raw Events drill adds a properties substring for **main_menu** so the explorer matches this definition.`}
          note="Use this to prioritize concept experiments and polish."
        >
          <RankTable
            rows={data.minutesPerClick}
            filters={filters}
            onOpenRawEvents={onOpenRawEvents}
          />
        </ChartCard>

        <ChartCard
          title="Survey Rating Distribution"
          criteria={`Source: deduped **survey_submit** with non-null **rating** (same rule as Concept Ranking **Surveys/game**): one unique survey per **device + interaction_session_id + game route/name** when submits are ≤ **2 minutes** apart; **last rating in the burst** wins; gap **> 2 min** = another unique survey.

Y-axis: count of those unique surveys grouped by final rating (1–5). **Raw Events** still lists every row.

Bar click: Raw Events → survey_submit with Properties substring matching that rating.`}
          note="To be done: survey events are currently wired elsewhere."
        >
          <BarChart
            data={[
              { label: "1", value: data.surveyRatings[0] ?? 0 },
              { label: "2", value: data.surveyRatings[1] ?? 0 },
              { label: "3", value: data.surveyRatings[2] ?? 0 },
              { label: "4", value: data.surveyRatings[3] ?? 0 },
              { label: "5", value: data.surveyRatings[4] ?? 0 },
            ]}
            barTooltip={(item) =>
              `Rating ${item.label}: ${Math.round(item.value)} — click: Raw Events → survey_submit filtered by this rating in Properties. Middle-click / Ctrl/Cmd+click: new tab.`
            }
            onBarClick={(item, _i, _si, e) =>
              onOpenRawEvents(
                {
                  eventName: "survey_submit",
                  propertiesContains: `"rating":${item.label}`,
                },
                e,
              )
            }
          />
        </ChartCard>

        <ChartCard
          title="Recent Survey Comments"
          criteria={`Last 5 survey_submit rows with non-empty properties.comment (same dashboard filters).

List only; no bar chart drill-down.`}
          note="To be done: survey events are currently wired elsewhere."
        >
          <ul className="list-disc pl-5 text-[13px] text-text-primary">
            {(data.surveyComments.length > 0
              ? data.surveyComments
              : ["No comments in current filter"]
            ).map((c, i) => (
              <li key={i} className="py-1">
                {c}
              </li>
            ))}
          </ul>
        </ChartCard>
      </div>
    </>
  );
}

type ConceptSortKey = "game" | "icon" | "tutorial" | "minStart" | "surveys" | "avgRating";

function RankTable({
  rows,
  filters,
  onOpenRawEvents,
}: {
  rows: Array<{
    game: string;
    value: number;
    iconClicks: number;
    tutorialPlay: number;
    tutorialDismiss: number;
    tutorialPlayPct: number | null;
    surveyCount: number;
    avgRating: number | null;
  }>;
  filters: FilterState;
  onOpenRawEvents: OpenRawEventsHandler;
}) {
  const [sortKey, setSortKey] = useState<ConceptSortKey>("minStart");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const toggleSort = (key: ConceptSortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "game" ? "asc" : "desc");
    }
  };

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const cmpNum = (a: number, b: number) => (a < b ? -dir : a > b ? dir : 0);
    const cmpStr = (a: string, b: string) => dir * a.localeCompare(b, undefined, { sensitivity: "base" });
    const nullLast = (a: number | null, b: number | null) => {
      if (a == null && b == null) return 0;
      if (a == null) return 1;
      if (b == null) return -1;
      return cmpNum(a, b);
    };

    return [...rows].sort((a, b) => {
      if (sortKey === "game") return cmpStr(a.game, b.game);
      if (sortKey === "icon") return cmpNum(a.iconClicks, b.iconClicks);
      if (sortKey === "tutorial") return nullLast(a.tutorialPlayPct, b.tutorialPlayPct);
      if (sortKey === "surveys") return cmpNum(a.surveyCount, b.surveyCount);
      if (sortKey === "avgRating") return nullLast(a.avgRating, b.avgRating);
      return cmpNum(a.value, b.value);
    });
  }, [rows, sortKey, sortDir]);

  if (sorted.length === 0) {
    return <p className="py-5 text-[13px] text-text-secondary">No data</p>;
  }

  const sortMark = (key: ConceptSortKey) => (sortKey === key ? (sortDir === "desc" ? " ▼" : " ▲") : "");

  return (
    <div>
      <table className="w-full border-collapse text-[11px]">
        <thead>
          <tr>
            <th className="border-b border-[#263a5a] px-0.5 py-1 text-left font-semibold text-text-secondary">
              <button
                type="button"
                className="text-left font-semibold text-text-secondary hover:text-text-primary"
                onClick={() => toggleSort("game")}
              >
                Game{sortMark("game")}
              </button>
            </th>
            <th className="w-9 border-b border-[#263a5a] px-0.5 py-1 text-left font-semibold text-text-secondary">
              Raw
            </th>
            <th className="border-b border-[#263a5a] px-0.5 py-1 text-right font-semibold text-text-secondary">
              <button
                type="button"
                className="w-full text-right font-semibold text-text-secondary hover:text-text-primary"
                onClick={() => toggleSort("icon")}
                title="Sort by popup_open tutorial count (main_menu only)"
              >
                Icons{sortMark("icon")}
              </button>
            </th>
            <th className="border-b border-[#263a5a] px-0.5 py-1 text-right font-semibold text-text-secondary">
              <button
                type="button"
                className="w-full text-right font-semibold text-text-secondary hover:text-text-primary"
                onClick={() => toggleSort("tutorial")}
                title="Sort by % play among popup_closed tutorial rows (main_menu only; play vs dismiss)"
              >
                Tut %{sortMark("tutorial")}
              </button>
            </th>
            <th className="border-b border-[#263a5a] px-0.5 py-1 text-right font-semibold text-text-secondary">
              <button
                type="button"
                className="w-full text-right font-semibold text-text-secondary hover:text-text-primary"
                onClick={() => toggleSort("minStart")}
              >
                Min/start{sortMark("minStart")}
              </button>
            </th>
            <th className="border-b border-[#263a5a] px-0.5 py-1 text-right font-semibold text-text-secondary">
              <button
                type="button"
                className="w-full text-right font-semibold text-text-secondary hover:text-text-primary"
                onClick={() => toggleSort("surveys")}
                title="Sort by unique surveys per game (2 min burst dedupe; last rating wins)"
              >
                Surveys/game{sortMark("surveys")}
              </button>
            </th>
            <th className="border-b border-[#263a5a] px-0.5 py-1 text-right font-semibold text-text-secondary">
              <button
                type="button"
                className="w-full text-right font-semibold text-text-secondary hover:text-text-primary"
                onClick={() => toggleSort("avgRating")}
                title="Sort by average unique-survey rating (1–5; last rating per 2 min burst)"
              >
                Rating{sortMark("avgRating")}
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.slice(0, 8).map((r) => {
            const gameOpts = conceptGameRawEventsOptions(r.game, "all");
            const iconOpts = tutorialPopupOpenOptions(r.game);
            const closedOpts = tutorialPopupClosedOptions(r.game);
            const surveyOpts = conceptGameRawEventsOptions(r.game, "survey_submit");
            return (
              <tr key={r.game}>
                <td className="border-b border-[#263a5a] px-0.5 py-1 text-text-primary">
                  {gameDisplayLabel(r.game)}
                </td>
                <td className="border-b border-[#263a5a] px-0.5 py-1">
                  <RawEventsAnchor
                    href={buildRawEventsUrlFromOptions(filters, gameOpts)}
                    title="Raw Events → all events for this game (route/name substring + game filter; middle-click / Ctrl/Cmd+click: new tab)"
                    ariaLabel="Open Raw Events for this game"
                    onOpen={(e) => onOpenRawEvents(gameOpts, e)}
                  />
                </td>
                <td className="border-b border-[#263a5a] px-0.5 py-1 text-right tabular-nums text-text-primary">
                  <div className="flex items-center justify-end gap-0.5">
                    <span>{r.iconClicks.toLocaleString()}</span>
                    <RawEventsAnchor
                      href={buildRawEventsUrlFromOptions(filters, iconOpts)}
                      title="Raw Events → popup_open (tutorial, main_menu) for this game (middle-click / Ctrl/Cmd+click: new tab)"
                      ariaLabel="Open Raw Events for main menu tutorial popup_open rows"
                      onOpen={(e) => onOpenRawEvents(iconOpts, e)}
                    />
                  </div>
                </td>
                <td className="border-b border-[#263a5a] px-0.5 py-1 text-right tabular-nums text-text-primary">
                  <div className="flex items-center justify-end gap-0.5">
                    <span>{formatTutorialPlayDismiss(r)}</span>
                    <RawEventsAnchor
                      href={buildRawEventsUrlFromOptions(filters, closedOpts)}
                      title="Raw Events → popup_closed (tutorial, main_menu) for this game (middle-click / Ctrl/Cmd+click: new tab)"
                      ariaLabel="Open Raw Events for main menu tutorial popup_closed rows"
                      onOpen={(e) => onOpenRawEvents(closedOpts, e)}
                    />
                  </div>
                </td>
                <td className="border-b border-[#263a5a] px-0.5 py-1 text-right tabular-nums text-text-primary">
                  {r.value.toFixed(2)}
                </td>
                <td className="border-b border-[#263a5a] px-0.5 py-1 text-right tabular-nums text-text-primary">
                  {r.surveyCount.toLocaleString()}
                </td>
                <td className="border-b border-[#263a5a] px-0.5 py-1 text-right tabular-nums text-text-primary">
                  <div className="flex items-center justify-end gap-0.5">
                    <span>{r.avgRating != null ? r.avgRating.toFixed(1) : "—"}</span>
                    {r.surveyCount > 0 ? (
                      <RawEventsAnchor
                        href={buildRawEventsUrlFromOptions(filters, surveyOpts)}
                        title="Raw Events → survey_submit for this game (middle-click / Ctrl/Cmd+click: new tab)"
                        ariaLabel="Open Raw Events for survey_submit rows"
                        onOpen={(e) => onOpenRawEvents(surveyOpts, e)}
                      />
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RawEventsAnchor({
  href,
  title,
  ariaLabel,
  onOpen,
}: {
  href: string;
  title: string;
  ariaLabel: string;
  onOpen: (e: MouseEvent<HTMLAnchorElement>) => void;
}) {
  return (
    <a
      href={href}
      className="inline-block rounded border border-border-subtle bg-panel-2 px-0.5 py-0 text-[10px] leading-tight text-text-primary no-underline hover:bg-[#223455]"
      title={title}
      aria-label={ariaLabel}
      onClick={(e) => {
        if (e.button !== 0) return;
        if (navigateOrOpenNewTab(href, e) === "new") {
          e.preventDefault();
          return;
        }
        e.preventDefault();
        onOpen(e);
      }}
      onAuxClick={(e) => {
        if (e.button !== 1) return;
        e.preventDefault();
        window.open(href, "_blank", "noopener");
      }}
    >
      ⧉
    </a>
  );
}

function tutorialPopupOpenOptions(gameKey: string): OpenRawEventsOptions {
  const base: OpenRawEventsOptions = {
    eventName: "popup_open",
    propertiesContains: "tutorial",
    propertiesContains2: "main_menu",
  };
  const g = gameFilterValueFromConceptKey(gameKey);
  if (g === "all") return base;
  return { ...base, gameUrl: g };
}

function tutorialPopupClosedOptions(gameKey: string): OpenRawEventsOptions {
  const base: OpenRawEventsOptions = {
    eventName: "popup_closed",
    propertiesContains: "tutorial",
    propertiesContains2: "main_menu",
  };
  const g = gameFilterValueFromConceptKey(gameKey);
  if (g === "all") return base;
  return { ...base, gameUrl: g };
}

/** Property substring for concept row key (route path or `game_name`). */
function conceptGamePropertyNeedle(gameKey: string): string | undefined {
  const t = gameKey.trim();
  if (!t || t === "(unknown)") return undefined;
  if (t.startsWith("/")) return t.replace(/\/+$/, "");
  return `"game_name":"${t}`;
}

/**
 * Concept Ranking drill-down: dashboard game filter plus a properties substring that
 * matches the row key in any property (`game_route`, `game_url`, `game_name`, …).
 * Avoids relying on `sg:` slug matching alone (e.g. `/games/word` URL variants).
 */
function conceptGameRawEventsOptions(gameKey: string, eventName: string): OpenRawEventsOptions {
  const opts: OpenRawEventsOptions = { eventName };
  const needle = conceptGamePropertyNeedle(gameKey);
  if (needle) opts.propertiesContains = needle;
  const g = gameFilterValueFromConceptKey(gameKey);
  if (g !== "all") opts.gameUrl = g;
  return opts;
}

function formatTutorialPlayDismiss(r: {
  tutorialPlayPct: number | null;
  tutorialPlay: number;
  tutorialDismiss: number;
}): string {
  const { tutorialPlayPct, tutorialPlay, tutorialDismiss } = r;
  const denom = tutorialPlay + tutorialDismiss;
  if (denom === 0) return "—";
  const pct =
    tutorialPlayPct != null && Number.isFinite(tutorialPlayPct)
      ? `${Math.round(tutorialPlayPct)}%`
      : "—";
  return `${pct} (${tutorialPlay}/${tutorialDismiss})`;
}
